import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import spIcon from "../../assets/brand/swlogo.png";
import spWordmark from "../../assets/brand/swletters.png";
import api from "../../api/api";
import { toast } from "react-toastify";

const OTP_LEN = 6; // backend sends 6-digit OTP

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export const AuthOtp = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const email = query.get("email") || "";

  const [code, setCode] = useState(Array(OTP_LEN).fill(""));
  const inputsRef = useRef([]);
  const [seconds, setSeconds] = useState(60);
  const [resending, setResending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (seconds <= 0) return;
    const id = setInterval(() => setSeconds((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [seconds]);

  const focusInput = (i) => inputsRef.current[i]?.focus();

  const handleChange = (i, val) => {
    const digit = val.replace(/\D/g, "").slice(0, 1);
    const next = [...code];
    next[i] = digit;
    setCode(next);
    if (digit && i < OTP_LEN - 1) focusInput(i + 1);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (!code[i] && i > 0) focusInput(i - 1);
      return;
    }
    if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusInput(i - 1);
    }
    if (e.key === "ArrowRight" && i < OTP_LEN - 1) {
      e.preventDefault();
      focusInput(i + 1);
    }
    if (e.key === "Enter") verify();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LEN);
    if (!pasted) return;
    const next = [...code];
    for (let i = 0; i < OTP_LEN; i++) next[i] = pasted[i] || "";
    setCode(next);
    const last = Math.min(pasted.length, OTP_LEN) - 1;
    if (last >= 0) focusInput(last);
  };

  const verify = async () => {
    const joined = code.join("");
    if (joined.length < OTP_LEN) return toast.warn("Enter the full code");

    try {
      setVerifying(true);
      await api.auth.verifyOtp(email, joined);
      // After OTP verified, go set password
      navigate(`/auth/set-password?email=${encodeURIComponent(email)}`);
    } catch (err) {
      console.warn("Verify OTP failed:", err?.status, err?.data?.message || err?.message);
      toast.error(err?.data?.message || "Invalid or expired OTP. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (seconds > 0 || resending) return;
    try {
      setResending(true);
      await api.auth.sendOtp(email);
      setSeconds(60);
    } catch (err) {
      console.warn("Resend OTP failed:", err?.status, err?.data?.message || err?.message);
      toast.error(err?.data?.message || "Failed to resend OTP. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
      <div className="bg-[#fff8e1] w-[412px] h-[917px] relative">
        <button
          type="button"
          aria-label="Go back"
          onClick={() => navigate(-1)}
          className="w-10 mt-12 h-10 rounded-full bg-[#ccb357] grid place-items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        {/* CONTENT BLOCK CENTERED */}
        <div className="absolute inset-0 pt-24 pb-24 flex flex-col items-center justify-center">
          <div className="w-full flex justify-center mb-9">
            <div className="flex items-end gap-0">
              <img src={spIcon} alt="" className="w-[60px] h-[36px]" />
              <img src={spWordmark} alt="Sweet Plate" className="h-[24px] w-[50px] -ml-1" />
            </div>
          </div>

          <h1 className="[font-family:'Nunito',Helvetica] font-extrabold text-[#5d4037] text-2xl mb-9 text-center">
            Please check your email
          </h1>

          <p className="mt-2 [font-family:'Nunito',Helvetica] text-[#5d4037] text-base text-center mb-7">
            We&apos;ve sent a code to <span className="font-semibold">{email}</span>
          </p>

          <div className="mt-6 mb-10 flex gap-3" onPaste={handlePaste}>
            {code.map((v, i) => (
              <input
                key={i}
                ref={(el) => (inputsRef.current[i] = el)}
                inputMode="numeric"
                pattern="[0-9]*"
                value={v}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="w-[56px] h-[56px] rounded-[10px] border-2 border-[#5d4037] bg-[#fff8e1] text-center [font-family:'Nunito',Helvetica] text-[#5d4037] text-2xl font-bold tracking-widest outline-none"
              />
            ))}
          </div>

          <button
            type="button"
            onClick={verify}
            disabled={verifying}
            className={`w-[260px] h-[52px] rounded-full font-bold text-[#5d4037] ${
              verifying ? "bg-[#d9c48c]" : "bg-[#f7c945] hover:opacity-90"
            }`}
          >
            {verifying ? "Verifying..." : "Verify"}
          </button>

          <div className="mt-6 [font-family:'Nunito',Helvetica] text-base">
            <span className="text-[#7a6f5c]">Didn&apos;t receive a code? </span>
            <button
              type="button"
              onClick={handleResend}
              disabled={seconds > 0 || resending}
              className="text-[#e53935] underline disabled:opacity-50 disabled:no-underline"
            >
              Resend
            </button>
          </div>

          <div className="mt-4 [font-family:'Nunito',Helvetica] text-[#7a6f5c] font-bold">
            Send code again <span className="tabular-nums">00:{String(seconds).padStart(2, "0")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthOtp;
